import { useCallback, useEffect, useState } from 'react';

import { motion } from 'framer-motion';

import { ConnectorProps } from '../types';

export function PipelineConnector({
    startRefs,
    endRefs,
    containerRef,
    isVisible,
    oneToOne = false,
    itemCount = 0,
    progressState,
}: ConnectorProps) {
    const [paths, setPaths] = useState<string[]>([]);

    const calculatePaths = useCallback(() => {
        if (!startRefs.current || !endRefs.current || !containerRef.current)
            return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const newPaths: string[] = [];

        if (oneToOne) {
            // 1:1 mapping: connect each start[i] to end[i]
            startRefs.current.forEach((startEl, index) => {
                const endEl = endRefs.current?.[index];
                if (!startEl || !endEl) return;

                const startRect = startEl.getBoundingClientRect();
                const endRect = endEl.getBoundingClientRect();

                const startX = startRect.right - containerRect.left;
                const startY =
                    startRect.top + startRect.height / 2 - containerRect.top;
                const endX = endRect.left - containerRect.left;
                const endY =
                    endRect.top + endRect.height / 2 - containerRect.top;

                const controlPointOffset = (endX - startX) / 2;
                const path = `M ${startX} ${startY} C ${startX + controlPointOffset} ${startY}, ${endX - controlPointOffset} ${endY}, ${endX} ${endY}`;

                newPaths.push(path);
            });
        } else {
            // Fan out: connect first start element to all end elements
            const startEl = startRefs.current[0];
            if (!startEl) return;

            const startRect = startEl.getBoundingClientRect();

            endRefs.current.forEach((endEl) => {
                if (!endEl) return;

                const endRect = endEl.getBoundingClientRect();

                const startX = startRect.right - containerRect.left;
                const startY =
                    startRect.top + startRect.height / 2 - containerRect.top;
                const endX = endRect.left - containerRect.left;
                const endY =
                    endRect.top + endRect.height / 2 - containerRect.top;

                const controlPointOffset = (endX - startX) / 2;
                const path = `M ${startX} ${startY} C ${startX + controlPointOffset} ${startY}, ${endX - controlPointOffset} ${endY}, ${endX} ${endY}`;

                newPaths.push(path);
            });
        }

        setPaths(newPaths);
    }, [startRefs, endRefs, containerRef, oneToOne]);

    useEffect(() => {
        calculatePaths();
        window.addEventListener('resize', calculatePaths);
        return () => window.removeEventListener('resize', calculatePaths);
    }, [calculatePaths]);

    // Recalculate when items change - multiple attempts to catch ref updates and height changes
    useEffect(() => {
        const timers = [
            setTimeout(calculatePaths, 50),
            setTimeout(calculatePaths, 150),
            setTimeout(calculatePaths, 300),
            setTimeout(calculatePaths, 500),
        ];
        return () => timers.forEach(clearTimeout);
    }, [isVisible, itemCount, progressState, calculatePaths]);

    if (!isVisible || paths.length === 0) return null;

    return (
        <svg className="absolute inset-0 pointer-events-none z-10 overflow-visible">
            {paths.map((path, index) => (
                <motion.path
                    key={`${index}-${path.slice(0, 20)}`}
                    d={path}
                    fill="none"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth="2"
                    strokeDasharray="6 4"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.4 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                />
            ))}
        </svg>
    );
}
